import { Observable } from 'rxjs';
import { EmptyResponse } from '../../core';
import { InjectionToken } from '../../injection';
import { MobizSettingsValue } from '../data/mobiz-settings-value.do';
import { MobizSettings } from '../data/mobiz-settings.do';
import { MobizSettingsScope } from '../other/mobiz-settings-scope.type';
import { MobizSettingsKey } from '../other/mobiz-settings.type';

/**
 * Generic mobiz settings service
 *
 * @see mobiz1:Mobiz.Web.Controllers.Api.SettingsController
 */
export interface IMobizSettingsService {

  /**
   * Updates the settings for the given scope.
   *
   * @deprecated Use IMobizSettingsService.save().
   *
   * @param scope The settings scope.
   * @param scopeId The ID of the scope entity.
   * @param key The settings key.
   * @param instance The instance of MobizSettings for the settings module.
   */
  add<T extends MobizSettingsValue, K extends string = MobizSettingsKey>(
    scope: MobizSettingsScope,
    scopeId: string,
    key: K,
    instance: MobizSettings<T>,
  ): Observable<MobizSettings<T>>;

  /**
   * Delete settings from a scope.
   *
   * @param scope The settings scope.
   * @param scopeId The ID of the scope entity.
   * @param key The settings key.
   */
  delete<K extends string = MobizSettingsKey>(scope: MobizSettingsScope, scopeId: string, key: K): Observable<EmptyResponse>;

  /**
   * Returns the settings for the given scope.
   *
   * @param scope The settings scope.
   * @param scopeId The ID of the scope entity.
   * @param key The settings key.
   * @param instance The instance of MobizSettings for the settings module.
   * @param disableErrorHandling Support disabling the default error handler.
   */
  get<T extends MobizSettingsValue, K extends string = MobizSettingsKey>(
    scope: MobizSettingsScope,
    scopeId: string,
    key: K,
    instance: MobizSettings<T>,
    disableErrorHandling?: boolean,
  ): Observable<MobizSettings<T>>;

  /**
   * Saves the settings for the given scope.
   *
   * @param scope The settings scope.
   * @param scopeId The ID of the scope entity.
   * @param key The settings key.
   * @param instance The instance of MobizSettings for the settings module.
   */
  save<T extends MobizSettingsValue, K extends string = MobizSettingsKey>(
    scope: MobizSettingsScope,
    scopeId: string,
    key: K,
    instance: MobizSettings<T>,
  ): Observable<MobizSettings<T>>;

  /**
   * Updates the settings for the given scope.
   *
   * @deprecated Use IMobizSettingsService.save().
   *
   * @param scope The settings scope.
   * @param scopeId The ID of the scope entity.
   * @param key The settings key.
   * @param instance The instance of MobizSettings for the settings module.
   */
  update<T extends MobizSettingsValue, K extends string = MobizSettingsKey>(
    scope: MobizSettingsScope,
    scopeId: string,
    key: K,
    instance: MobizSettings<T>,
  ): Observable<MobizSettings<T>>;
}

export const IMobizSettingsService = new InjectionToken('IMobizSettingsService');
